import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ThemeToggle from './ThemeToggle';
import Avatar from './Avatar';
import { Icon } from './Icon';
import type { IconName } from './Icon';

/** Explicitly open the user menu (idempotent). Used by onboarding tours. */
export const openUserMenu = () => window.dispatchEvent(new CustomEvent('bp:open-user-menu'));
/** Explicitly close the user menu (idempotent). Used by onboarding tours. */
export const closeUserMenu = () => window.dispatchEvent(new CustomEvent('bp:close-user-menu'));

export interface SidebarUserMenuSubItem {
    label: string;
    onClick: () => void;
    /** Shows a check mark on the active choice (e.g. the current language). */
    selected?: boolean;
    /** Optional leading mark, e.g. the language flag in the picker
     *  (`<LanguageFlag lang=… />` — not an emoji: Windows draws flag emoji as
     *  two letters). */
    flag?: React.ReactNode;
}

export interface SidebarUserMenuItem {
    /** Material Symbols icon name. */
    icon: IconName;
    label: string;
    /** Top-level action. Ignored when ``submenu`` is set (the row expands the
     *  submenu instead of acting). */
    onClick?: () => void;
    /** Renders red + a top-border separator (used for "logout"). */
    danger?: boolean;
    /** When present, the row toggles an inline submenu instead of acting —
     *  used for the per-user language picker in the tenant. */
    submenu?: SidebarUserMenuSubItem[];
    /** Optional HTML id for the button — used by onboarding tours to target
     *  specific menu items with Driver.js. */
    id?: string;
}

export interface SidebarUserMenuProps {
    /** Photo URL. Empty → initials (see `Avatar`). */
    avatarUrl?: string | null;
    /** Name shown on the trigger (usually just the first name). */
    displayName: string;
    /** Full name the avatar initials and colour come from. Defaults to
     *  `displayName`. */
    avatarName?: string;
    /** Short role/status line under the name (e.g. "Admin", "Propietario"). */
    roleLabel: string;
    /** Tailwind text-colour class for the role line. Default: muted slate. */
    roleTintClass?: string;
    /** Dropdown entries, in order. The component closes the menu before
     *  invoking ``onClick`` so callers just navigate/act. */
    items: SidebarUserMenuItem[];
    triggerAriaLabel: string;
    menuAriaLabel: string;
}

const ITEM_CLASS =
    'w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-surface-hover-dark transition-colors';
const ITEM_ICON_CLASS = '!text-[20px] text-slate-500';
/** Room (px) kept between the menu / flyout and the window edge. */
const VIEWPORT_MARGIN = 8;
/** The `mb-2` between the menu and the trigger block, in px. */
const MENU_GAP = 8;
/** Tailwind `sm`: from here up a submenu is a flyout to the right; below it,
 *  an inline panel inside the menu. Must match the `sm:` classes below. */
const FLYOUT_MEDIA = '(min-width: 640px)';

const DANGER_CLASS =
    'w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors border-t border-border-strong dark:border-border-strong-dark';

/**
 * Bottom-of-sidebar user block: avatar + name/role trigger that opens an
 * UPWARD dropdown menu, with the theme toggle alongside. Shared shell for
 * tenant + admin — each app computes the avatar URL, display name, role
 * label and menu items and hands them in as props (tenant uses `t()` +
 * tenant roles; admin uses Spanish literals + `is_platform_admin`).
 *
 * Owns the dropdown open/close state, click-outside dismissal and the
 * a11y wiring so both apps stay in sync.
 */
const SidebarUserMenu: React.FC<SidebarUserMenuProps> = ({
    avatarUrl,
    displayName,
    roleLabel,
    roleTintClass = 'text-slate-500',
    items,
    triggerAriaLabel,
    menuAriaLabel,
    avatarName,
}) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [openSub, setOpenSub] = useState<string | null>(null);
    const flyoutRef = useRef<HTMLDivElement>(null);
    const [menuMaxHeight, setMenuMaxHeight] = useState<number | undefined>(undefined);
    const [flyoutStyle, setFlyoutStyle] = useState<React.CSSProperties | undefined>(undefined);

    // The menu opens UPWARD from the trigger, so its ceiling is the space
    // above the trigger block. Past it the menu scrolls instead of running
    // off the top of the window — which is what happens on a short phone
    // screen once the inline language panel (nine languages) opens.
    useLayoutEffect(() => {
        if (!open) return;
        const fit = () => {
            const wrapper = wrapperRef.current;
            if (!wrapper) return;
            const room = wrapper.getBoundingClientRect().top - MENU_GAP - VIEWPORT_MARGIN;
            setMenuMaxHeight(Math.max(room, 0));
        };
        fit();
        window.addEventListener('resize', fit);
        return () => window.removeEventListener('resize', fit);
    }, [open]);

    // A flyout starts level with its row, but the row sits near the bottom of
    // the window (the menu lives at the foot of the sidebar), so a long list —
    // the nine languages — used to run past the bottom edge. Clamp it: slide
    // it up until it fits, and past the window's height cap it and scroll.
    // Below `sm` the submenu is an inline panel: just bring it into view.
    useLayoutEffect(() => {
        if (!openSub) {
            setFlyoutStyle(undefined);
            return;
        }
        const place = () => {
            const flyout = flyoutRef.current;
            const row = flyout?.parentElement;
            if (!flyout || !row) return;
            if (!window.matchMedia(FLYOUT_MEDIA).matches) {
                setFlyoutStyle(undefined);
                flyout.scrollIntoView({ block: 'nearest' });
                return;
            }
            const viewport = window.innerHeight;
            const maxHeight = Math.max(viewport - 2 * VIEWPORT_MARGIN, 0);
            // scrollHeight leaves the border out; max-height (border-box) counts it.
            const border = flyout.offsetHeight - flyout.clientHeight;
            const height = Math.min(flyout.scrollHeight + border, maxHeight);
            const rowTop = row.getBoundingClientRect().top;
            const top = Math.max(
                VIEWPORT_MARGIN,
                Math.min(rowTop, viewport - VIEWPORT_MARGIN - height),
            );
            setFlyoutStyle({ top: top - rowTop, maxHeight });
        };
        place();
        window.addEventListener('resize', place);
        return () => window.removeEventListener('resize', place);
    }, [openSub]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            // While a guided tour is active, the tour controls the menu.
            if ((window as unknown as { __bpTourMenuLocked?: boolean }).__bpTourMenuLocked) return;
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setOpen(false);
                setOpenSub(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Explicit open/close control for onboarding tours (idempotent — no toggle,
    // so tour state can never desync the way a `.click()` toggle would).
    useEffect(() => {
        const doOpen = () => setOpen(true);
        const doClose = () => { setOpen(false); setOpenSub(null); };
        window.addEventListener('bp:open-user-menu', doOpen);
        window.addEventListener('bp:close-user-menu', doClose);
        return () => {
            window.removeEventListener('bp:open-user-menu', doOpen);
            window.removeEventListener('bp:close-user-menu', doClose);
        };
    }, []);

    const handleItem = (item: SidebarUserMenuItem) => {
        if (item.submenu) {
            setOpenSub((cur) => (cur === item.label ? null : item.label));
            return;
        }
        setOpen(false);
        setOpenSub(null);
        item.onClick?.();
    };

    const handleSubItem = (sub: SidebarUserMenuSubItem) => {
        setOpen(false);
        setOpenSub(null);
        sub.onClick();
    };

    return (
        <div className="relative" ref={wrapperRef}>
            {open && (
                <div
                    id="bp-user-menu"
                    role="menu"
                    aria-label={menuAriaLabel}
                    style={{ maxHeight: menuMaxHeight }}
                    className={`absolute left-2 right-2 bottom-full mb-2 bg-surface-card-light dark:bg-surface-card-dark border border-border-strong dark:border-border-strong-dark rounded-xl shadow-2xl overflow-y-auto z-50 animate-in fade-in zoom-in-95 duration-150 ${openSub ? 'sm:overflow-visible' : ''}`}
                >
                    {items.map((item) => {
                        // Plain action row (everything except the language picker).
                        if (!item.submenu) {
                            return (
                                <button
                                    key={item.label}
                                    id={item.id}
                                    type="button"
                                    onClick={() => handleItem(item)}
                                    className={item.danger ? DANGER_CLASS : ITEM_CLASS}
                                >
                                    <Icon name={item.icon} className={item.danger ? '!text-[20px]' : ITEM_ICON_CLASS} />
                                    <span className="flex-1 text-left">{item.label}</span>
                                </button>
                            );
                        }
                        // Submenu row: the row stays a single line and the choices
                        // fly out to the RIGHT (desktop) so the menu never grows,
                        // clamped to the window (see `place` above); on mobile
                        // (< sm) the flyout falls back to an inline panel below
                        // the row, and the menu scrolls if it gets too tall.
                        const isOpen = openSub === item.label;
                        return (
                            <div key={item.label} className="relative">
                                <button
                                    id={item.id}
                                    type="button"
                                    onClick={() => handleItem(item)}
                                    className={ITEM_CLASS}
                                    aria-haspopup="menu"
                                    aria-expanded={isOpen}
                                >
                                    <Icon name={item.icon} className={ITEM_ICON_CLASS} />
                                    <span className="flex-1 text-left">{item.label}</span>
                                    <Icon name="chevron_right" className="!text-[18px] text-slate-400" />
                                </button>
                                {isOpen && (
                                    <div
                                        ref={flyoutRef}
                                        role="menu"
                                        style={flyoutStyle}
                                        className="border-t border-border-subtle dark:border-border-subtle-dark bg-slate-50 dark:bg-surface-hover-dark sm:absolute sm:left-full sm:top-0 sm:ml-1 sm:w-56 sm:border sm:border-border-strong sm:dark:border-border-strong-dark sm:rounded-xl sm:shadow-2xl sm:bg-surface-card-light sm:dark:bg-surface-card-dark sm:overflow-y-auto"
                                    >
                                        {item.submenu.map((sub) => (
                                            <button
                                                key={sub.label}
                                                type="button"
                                                role="menuitemradio"
                                                aria-checked={!!sub.selected}
                                                onClick={() => handleSubItem(sub)}
                                                className={`w-full flex items-center gap-3 pl-12 pr-4 sm:pl-3.5 sm:pr-3.5 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-surface-dark transition-colors ${sub.selected ? 'sm:bg-slate-100 sm:dark:bg-surface-dark' : ''}`}
                                            >
                                                {sub.flag && (
                                                    <span className="inline-flex shrink-0 items-center" aria-hidden="true">{sub.flag}</span>
                                                )}
                                                <span className="flex-1 text-left">{sub.label}</span>
                                                {sub.selected && (
                                                    <Icon name="check" className="!text-[18px] text-primary" />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="flex items-center gap-2 p-2 border-t border-border-subtle dark:border-border-subtle-dark">
                <button
                    id="bp-user-menu-trigger"
                    type="button"
                    onClick={() => setOpen((o) => { if (o) setOpenSub(null); return !o; })}
                    className="flex-1 flex items-center gap-2 p-1 pl-1 pr-2 rounded-xl border border-border-strong bg-surface-card-light hover:border-primary dark:border-border-strong-dark dark:bg-surface-card-dark dark:hover:border-primary transition-colors min-w-0"
                    aria-label={triggerAriaLabel}
                    aria-haspopup="menu"
                    aria-expanded={open}
                >
                    <Avatar
                        src={avatarUrl}
                        name={avatarName || displayName}
                        size={36}
                        shape="rounded"
                        className="ring-2 ring-surface-light dark:ring-slate-700 shadow-sm"
                    />
                    <div className="flex flex-col min-w-0 text-left">
                        <span className="text-xs font-bold text-slate-800 dark:text-white truncate">
                            {displayName}
                        </span>
                        <span className={`text-[10px] font-bold uppercase tracking-widest truncate ${roleTintClass}`}>
                            {roleLabel}
                        </span>
                    </div>
                </button>
                <div className="shrink-0">
                    <ThemeToggle />
                </div>
            </div>
        </div>
    );
};

export default SidebarUserMenu;
